import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiCopy, FiRefreshCw } from 'react-icons/fi';
import { getTrialPlans, getTrialAccounts, createTrialAccount } from '../../services/api';
import styles from './TrialAccountsPage.module.css';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const daysRemaining = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const emptyForm = { name: '', email: '', phone: '', derasarName: '', city: '', state: '', trialPlanId: '' };

const TrialAccountsPage = () => {
  const [accounts, setAccounts]     = useState([]);
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, planRes] = await Promise.all([getTrialAccounts(), getTrialPlans()]);
      setAccounts(accRes.data.data || []);
      setPlans(planRes.data.data || []);
    } catch {
      toast.error('Failed to load trial accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setForm(emptyForm);
    setCredentials(null);
    setShowModal(true);
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await createTrialAccount(form);
      setCredentials(res.data.data);
      toast.success('Trial account created');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create trial account');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Trial Accounts</h2>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
            Manually create short-duration trial admin accounts
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load} title="Refresh">
            <FiRefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={openModal}>
            <FiPlus size={14} /> Create Trial Account
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : accounts.length === 0 ? (
        <div className="empty-state"><p>No trial accounts yet.</p></div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Derasar</th>
                  <th>Admin</th>
                  <th>Plan</th>
                  <th>Expires</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => {
                  const sub = a.subscription || {};
                  const remaining = daysRemaining(sub.endDate);
                  const expired = remaining !== null && remaining < 0;
                  return (
                    <tr key={a.id}>
                      <td>
                        <div className={styles.cellName}>{a.name}</div>
                        <div className={styles.cellSub}>{a.city}</div>
                      </td>
                      <td>
                        <div className={styles.cellSub}>{a.admin?.auth?.email || '—'}</div>
                      </td>
                      <td>{sub.trialPlan?.label || '—'}</td>
                      <td>{fmtDate(sub.endDate)}</td>
                      <td>
                        <span className={`badge badge-${expired ? 'rejected' : 'approved'}`}>
                          {expired ? 'Expired' : `${remaining ?? '—'} days left`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create Trial Account</h3>

            {credentials ? (
              <div className={styles.credBox}>
                <div className={styles.credRow}>
                  <span className={styles.credLabel}>Login URL</span>
                  <span className={styles.credValue}>{credentials.loginUrl}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(credentials.loginUrl, 'Login URL')}><FiCopy size={12} /></button>
                </div>
                <div className={styles.credRow}>
                  <span className={styles.credLabel}>Email</span>
                  <span className={styles.credValue}>{credentials.email}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(credentials.email, 'Email')}><FiCopy size={12} /></button>
                </div>
                <div className={styles.credRow}>
                  <span className={styles.credLabel}>Temporary Password</span>
                  <span className={styles.credValue}>{credentials.tempPassword}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(credentials.tempPassword, 'Password')}><FiCopy size={12} /></button>
                </div>
                <div className="modal-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => setShowModal(false)}>Done</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input name="phone" value={form.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Derasar Name *</label>
                  <input name="derasarName" value={form.derasarName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input name="city" value={form.city} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input name="state" value={form.state} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Trial Plan *</label>
                  <select name="trialPlanId" value={form.trialPlanId} onChange={handleChange} required>
                    <option value="">Select a plan</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialAccountsPage;
